import { useState, useEffect, createContext, useContext } from 'react';
import { auth } from './firebaseConfig';
import { getLimitsForPlan, LEGACY_PLAN_MAP } from './planConfig';
import { getQuotaStatus } from './quotaManager';
import { resolveSubscriptionDoc } from './subscriptionDoc';

const UserContext = createContext();
const KNOWN_PLANS = new Set(['demo', 'basic', 'pro', 'research']);

function normalizePlanId(plan) {
  return String(plan || '').trim().toLowerCase();
}

export const UserProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userDetails, setUserDetails] = useState({
    isSubscribed: false,
    planType: null,
    endDate: null,
    startDate: null,
    status: 'Inactive',
    planLimits: null,
    quota: null,
    reportsCombo: null, // kept temporarily for backward compat
  });

  const defaultDetails = {
    isSubscribed: false,
    planType: null,
    endDate: null,
    startDate: null,
    status: 'Inactive',
    planLimits: null,
    quota: null,
    reportsCombo: null,
  };

  const fetchUserSubscription = async () => {
    try {
      const user = auth.currentUser;
      if (!user) { setUserDetails(defaultDetails); return; }

      const { snap: docSnap } = await resolveSubscriptionDoc(user.phoneNumber);

      if (!docSnap.exists()) { setUserDetails(defaultDetails); return; }

      const subscriptionData = docSnap.data();
      const currentTime = new Date();

      // Parse dates
      const parseDate = (raw) => {
        if (!raw) return null;
        if (raw.toDate) return raw.toDate();
        if (raw instanceof Date) return raw;
        return new Date(raw);
      };

      const endDate = parseDate(subscriptionData.endDate);
      const startDate = parseDate(subscriptionData.startDate);

      const isSubscribed = endDate && endDate.getTime() > currentTime.getTime();
      const computedStatus = isSubscribed ? 'Active' : 'Expired';

      // Resolve effective plan id.
      // 1) known current plan
      // 2) known legacy alias -> target
      // 3) fallback to quota.syncedPlan if valid
      // 4) active unknown plans default to basic; expired unknown plans default to demo
      const rawPlanId = normalizePlanId(subscriptionData.plan);
      const mappedPlanId = LEGACY_PLAN_MAP[rawPlanId] || rawPlanId;
      const syncedPlanId = normalizePlanId(subscriptionData?.quota?.syncedPlan);

      let planId = mappedPlanId;
      if (!KNOWN_PLANS.has(planId)) {
        if (KNOWN_PLANS.has(syncedPlanId)) {
          planId = syncedPlanId;
        } else {
          planId = isSubscribed ? 'basic' : 'demo';
        }
      }
      const trialWindowMs = 4 * 24 * 60 * 60 * 1000;
      const looksLikeTrialDuration =
        !!(startDate && endDate && (endDate.getTime() - startDate.getTime()) <= trialWindowMs);
      const isRealTrial = subscriptionData.isTrial === true && looksLikeTrialDuration;
      if (isSubscribed && planId === 'demo' && !isRealTrial) {
        planId = 'basic';
      }

      // Keep refresh path read-only for quota to prevent accidental limit rewrites.
      // Quota is updated only by explicit actions (plan purchase, add-on, quota consume).

      // Compute plan limits
      const planLimits = getLimitsForPlan(planId);

      // Fetch quota status
      let quota = null;
      try {
        quota = await getQuotaStatus();
      } catch (e) {
        console.error('Error fetching quota:', e);
      }

      setUserDetails({
        isSubscribed,
        planType: planId || subscriptionData.plan || 'N/A',
        endDate,
        startDate,
        status: computedStatus,
        planLimits,
        quota,
        reportsCombo: subscriptionData.reportsCombo || null,
      });
    } catch (error) {
      console.error('Error fetching subscription details:', error);
      setUserDetails(defaultDetails);
    }
  };

  const refreshQuota = async () => {
    try {
      const quota = await getQuotaStatus();
      setUserDetails(prev => ({ ...prev, quota }));
    } catch (e) {
      console.error('Error refreshing quota:', e);
    }
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(user => {
      setIsLoggedIn(!!user);
      if (user) {
        fetchUserSubscription();
      } else {
        setUserDetails(defaultDetails);
      }
    });

    return () => unsubscribe();
  }, []);

  return (
    <UserContext.Provider
      value={{
        isLoggedIn,
        ...userDetails,
        refreshUserDetails: fetchUserSubscription,
        checkSubscriptionStatus: fetchUserSubscription,
        refreshQuota,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUserContext = () => useContext(UserContext);
